/**
 * Phase 18.6 → 18.15.4 — Branding (logo + favicon + footer logo +
 * homepage hero + site metadata) hook.
 *
 * Fetches /api/website/branding once on mount and:
 *  1. exposes the active logo / footer-logo / hero / favicon URLs via
 *     ``useBranding()`` so the Header, Footer and Homepage can render
 *     the admin-configured assets with graceful fallbacks;
 *  2. dynamically rewrites the document's <link rel="icon"> tags so the
 *     browser tab updates after an admin edits the favicon (cache-busted
 *     via the ``?v=...`` query string the backend emits);
 *  3. updates ``document.title`` from the central metadata block so
 *     admins can edit the browser-tab title + tagline without code.
 *
 * Provider is mounted in App.js so every route shares one fetch.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hardcoded fallback — the existing 5999Cargo wordmark.
const FALLBACK_LOGO_URL =
  "https://customer-assets.emergentagent.com/job_cargo-to-curacao/artifacts/fp0bzc3n_Logo-5999Cargo.png";
const FALLBACK_LOGO_ALT = "5999Cargo Group Europe — Cargo with a smile";

const FALLBACK_METADATA = {
  site_title: "5999Cargo Group Europe",
  site_tagline: "Cargo with a smile.",
  browser_title_template: "{page_title} | 5999Cargo",
  default_meta_description:
    "Verzend pakketten, dozen, pallets en zakelijke cargo vanuit Nederland naar de Caribbean via 5999Cargo. Realtime tracking, all-in tarieven en Mijn 5999Cargo.",
};

const BrandingContext = createContext({
  logoUrl: FALLBACK_LOGO_URL,
  logoAlt: FALLBACK_LOGO_ALT,
  footerLogoUrl: FALLBACK_LOGO_URL,
  footerLogoAlt: FALLBACK_LOGO_ALT,
  homepageHeroUrl: null,
  homepageHeroAlt: "",
  faviconUrl: null,
  hasCustomLogo: false,
  hasCustomFooterLogo: false,
  hasCustomHomepageHero: false,
  hasCustomFavicon: false,
  metadata: FALLBACK_METADATA,
  refresh: () => {},
});

function buildUrl(assetUrl) {
  if (!assetUrl) return null;
  if (/^https?:\/\//i.test(assetUrl)) return assetUrl;
  return `${BACKEND_URL}${assetUrl}`;
}

function ensureLink(rel, href) {
  const existing = document.querySelectorAll(`link[rel='${rel}']`);
  existing.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
  const link = document.createElement("link");
  link.rel = rel;
  link.href = href;
  document.head.appendChild(link);
}

export function BrandingProvider({ children }) {
  const [state, setState] = useState({
    logo: null,
    footer_logo: null,
    homepage_hero: null,
    favicon: null,
    metadata: FALLBACK_METADATA,
  });

  const load = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/website/branding`);
      setState({
        logo: r.data?.logo || null,
        footer_logo: r.data?.footer_logo || null,
        homepage_hero: r.data?.homepage_hero || null,
        favicon: r.data?.favicon || null,
        metadata: { ...FALLBACK_METADATA, ...(r.data?.metadata || {}) },
      });
    } catch {
      setState({
        logo: null,
        footer_logo: null,
        homepage_hero: null,
        favicon: null,
        metadata: FALLBACK_METADATA,
      });
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const custom = state.favicon ? buildUrl(state.favicon.url) : null;
    if (custom) {
      ensureLink("icon", custom);
      ensureLink("shortcut icon", custom);
    } else {
      ensureLink("icon", "/favicon.ico");
    }
  }, [state.favicon]);

  // Phase 18.15.4 — apply default tab title from metadata.
  //
  // SEO Mini-Sprint (Feb 2026) — only set the document title here when
  // NO page-level <SEO /> has set a custom title yet. Previously this
  // useEffect would unconditionally overwrite `document.title` after
  // metadata loaded, which caused a race with the page's own <SEO />
  // and made many public pages briefly fall back to the homepage title
  // until the next re-render. Now we only set the title if it is still
  // empty or equal to the document's initial fallback set by `index.html`.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const m = state.metadata || FALLBACK_METADATA;
    const fallbackTitle = m.site_tagline
      ? `${m.site_title} — ${m.site_tagline}`
      : m.site_title;
    if (!fallbackTitle) return;
    // Only seed the title if a page-level <SEO /> hasn't already taken
    // ownership. We treat the empty string and the historical
    // index.html fallback ("5999Cargo Group Europe — Cargo with a
    // smile.") as "no page title yet".
    const cur = document.title || "";
    const isUntouched =
      cur === ""
      || cur === fallbackTitle
      || cur === "5999Cargo Group Europe — Cargo with a smile.";
    if (isUntouched) {
      document.title = fallbackTitle;
    }
  }, [state.metadata]);

  const value = useMemo(() => {
    const logoUrl = state.logo ? buildUrl(state.logo.url) : FALLBACK_LOGO_URL;
    // Footer fallback chain: footer_logo → header logo → wordmark
    const footerLogoUrl = state.footer_logo
      ? buildUrl(state.footer_logo.url)
      : logoUrl;
    const homepageHeroUrl = state.homepage_hero
      ? buildUrl(state.homepage_hero.url)
      : null;
    const faviconUrl = state.favicon ? buildUrl(state.favicon.url) : null;
    return {
      logoUrl,
      logoAlt: FALLBACK_LOGO_ALT,
      footerLogoUrl,
      footerLogoAlt: state.footer_logo?.alt_text || FALLBACK_LOGO_ALT,
      homepageHeroUrl,
      homepageHeroAlt: state.homepage_hero?.alt_text || "",
      faviconUrl,
      hasCustomLogo: !!state.logo,
      hasCustomFooterLogo: !!state.footer_logo,
      hasCustomHomepageHero: !!state.homepage_hero,
      hasCustomFavicon: !!state.favicon,
      metadata: state.metadata || FALLBACK_METADATA,
      refresh: load,
    };
  }, [state]);

  return (
    <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
